/**
 * Real-Time Chat Hooks
 *
 * Custom hooks for real-time chat functionality including message sync,
 * typing indicators, and presence tracking.
 *
 * @module @municipality/chat/useRealTimeChat
 */

import { useEffect, useRef, useState } from 'react';
import type { Unsubscribe } from 'firebase/firestore';
import type { Conversation, ChatScope } from '@municipality/types';
import { chatService } from './chat';
import { presenceService } from './presence';
import { useChatStore } from './chatStore';

export const useRealTimeConversations = (
  userId: string | undefined,
  scope: ChatScope,
  municipalityId?: string
): void => {
  const setConversations = useChatStore((state) => state.setConversations);
  const setUnreadCount = useChatStore((state) => state.setUnreadCount);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  useEffect(() => {
    if (!userId) return;
    unsubscribeRef.current = chatService.onConversationsSnapshot(
      userId,
      scope,
      municipalityId,
      (conversations) => {
        setConversations(conversations);
        const totalUnread = conversations.reduce(
          (sum, conv) => sum + (conv.unreadCount[userId] ?? 0),
          0
        );
        setUnreadCount(totalUnread);
      }
    );
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [userId, scope, municipalityId]);
};

export const useRealTimeMessages = (
  conversationId: string | null,
  userId: string | undefined
): { isLoading: boolean } => {
  const setMessages = useChatStore((state) => state.setMessages);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);
  const activeConversationRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!conversationId || !userId) {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
        activeConversationRef.current = null;
      }
      return;
    }
    if (activeConversationRef.current === conversationId && unsubscribeRef.current) {
      return;
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setIsLoading(true);
    activeConversationRef.current = conversationId;
    unsubscribeRef.current = chatService.onMessagesSnapshot(conversationId, userId, (messages) => {
      setMessages(conversationId, messages);
      setIsLoading(false);
    });
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
        activeConversationRef.current = null;
      }
    };
  }, [conversationId, userId]);

  return { isLoading };
};

export const useTypingIndicator = (conversationId: string | null) => {
  const typingUsers = useChatStore((state) =>
    conversationId ? state.typingUsers[conversationId] ?? [] : []
  );
  const setTypingStatus = useChatStore((state) => state.setTypingStatus);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setTyping = async (userId: string, isTyping: boolean) => {
    if (!conversationId) return;
    if (isTyping) {
      await setTypingStatus(conversationId, userId, true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(async () => {
        await setTypingStatus(conversationId, userId, false);
      }, 3000);
    } else {
      await setTypingStatus(conversationId, userId, false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  return { typingUsers, setTyping };
};

export const usePresence = (
  userId: string | undefined,
  scope: ChatScope,
  municipalityId?: string
) => {
  const updatePresence = useChatStore((state) => state.updatePresence);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!userId) return;
    cleanupRef.current = presenceService.initializePresence(userId, scope, municipalityId);
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [userId, scope, municipalityId]);

  return { updatePresence };
};

export const useUserPresences = (userIds: string[]) => {
  const updatePresence = useChatStore((state) => state.updatePresence);
  const userPresences = useChatStore((state) => state.userPresences);
  const unsubscribesRef = useRef<Unsubscribe[]>([]);

  useEffect(() => {
    if (userIds.length === 0) return;
    unsubscribesRef.current.forEach((unsub) => unsub());
    unsubscribesRef.current = [];
    unsubscribesRef.current = presenceService.onPresenceSnapshot(userIds, (presences) => {
      presences.forEach((presence) => {
        updatePresence(presence.userId, presence);
      });
    });
    return () => {
      unsubscribesRef.current.forEach((unsub) => unsub());
      unsubscribesRef.current = [];
    };
  }, [userIds.join(','), updatePresence]);

  return userPresences;
};

export const useConversationTyping = (conversation: Conversation | null) => {
  const setTypingUsers = useChatStore((state) => state.setTypingUsers);

  useEffect(() => {
    if (!conversation) return;
    setTypingUsers(conversation.id, conversation.typingUsers);
  }, [conversation?.id, conversation?.typingUsers.join(','), setTypingUsers]);

  return conversation?.typingUsers ?? [];
};
